import React, { useContext, useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  ReloadOutlined,
  RightOutlined,
  DashboardOutlined,
  LoadingOutlined,
  UpOutlined,
  DownOutlined
} from '@ant-design/icons';
import {
  Button,
  Table,
  Space,
  Row,
  Col,
  Card,
  Tag,
  Alert,
  Typography,
  Grid,
  Badge,
  Tooltip
} from 'antd';
import FiltersPanel from '../../Components/FiltersPanel';
import ExportButton from '../../Components/ExportButton';
import {
  DistributionChart,
  InventoryHealthGauge,
  CriticalTrendLine,
  ProviderRankingChart,
  StockoutForecastChart,
  VelocityOfChangeChart,
  ProviderHeatmap,
  ImprovementMomentumChart,
  HealthEvolutionChart,
  BufferLevelSignalChart
} from '../../Components/Charts';
import { useSkusRecords } from '../../hooks/useSkusRecords';
import { useReportFilters } from '../../hooks/useReportFilters';
import { formatDate, formatPercent } from '../../utils/dateFormatters';
import { SET_REPORT_FILTERS } from '../../utils/Types';
import UserContext from '../../utils/Contexts/UserContext';

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

/**
 * DailyInventoryDistributionReport - Main page component (Responsive)
 */
function DailyInventoryDistributionReport() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const user = useContext(UserContext);

  // Responsive helpers
  const isXs = !screens.xs;
  const isSm = screens.sm;

  // Get filters from Redux store
  const { filters, updateFilter, reset } = useReportFilters('dailyInventoryDistribution');

  // Limpiar filtros de Redux al desmontar el componente
  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  // Local state for client-side sorting
  const [localSortField, setLocalSortField] = useState(null);
  const [localSortOrder, setLocalSortOrder] = useState(null);

  // Collapse state for sections and individual charts
  const [collapsedSections, setCollapsedSections] = useState({});
  const [collapsedCharts, setCollapsedCharts] = useState({});

  const toggleSection = (key) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleChart = (key) => {
    setCollapsedCharts(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Section header component
  const SectionHeader = ({ title, sectionKey }) => {
    const isCollapsed = collapsedSections[sectionKey];
    return (
      <div
        onClick={() => toggleSection(sectionKey)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: isXs ? '10px 14px' : '12px 20px',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.2s ease',
          marginBottom: isXs ? 12 : 16
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f9fafb';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#ffffff';
          e.currentTarget.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.04)';
        }}
      >
        {isCollapsed
          ? <DownOutlined style={{ fontSize: isXs ? 12 : 14, color: '#6b7280' }} />
          : <UpOutlined style={{ fontSize: isXs ? 12 : 14, color: '#6b7280' }} />
        }
        <Text style={{ fontSize: isXs ? 15 : 17, fontWeight: 600, color: '#1f2937' }}>
          {title}
        </Text>
        <Text type="secondary" style={{ fontSize: isXs ? 11 : 12, marginLeft: 'auto' }}>
          {isCollapsed ? 'Click para expandir' : 'Click para colapsar'}
        </Text>
      </div>
    );
  };

  // Toggle button for individual charts
  const ChartToggleButton = ({ chartKey }) => {
    const isCollapsed = collapsedCharts[chartKey];
    return (
      <Button
        type="text"
        size="small"
        icon={isCollapsed ? <DownOutlined /> : <UpOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          toggleChart(chartKey);
        }}
        style={{ color: '#6b7280', fontSize: isXs ? 10 : 11 }}
      >
        {isCollapsed ? 'Abrir' : 'Cerrar'}
      </Button>
    );
  };

  // Fetch data
  const { data, isLoading, isError, error, refetch } = useSkusRecords({
    reportType: 'dailyInventoryDistribution',
    filters: {
      ...filters,
      groupByNit: filters.groupByNit ?? false,
      onlyActive: filters.onlyActive ?? true
    },
    pagination: { page: 1, limit: 10000 },
    enabled: !!(filters.dateRange?.initialDate && filters.dateRange?.finalDate)
  });

  // Handle apply filters
  const handleApplyFilters = () => {
    refetch();
  };

  // Handle reset filters
  const handleReset = () => {
    reset();
  };

  // Handle sort (local only - no backend call)
  const handleSort = (column) => {
    const newOrder = localSortField === column && localSortOrder === 'ascend' ? 'descend' : 'ascend';
    setLocalSortField(column);
    setLocalSortOrder(newOrder);
  };

  // Sort data locally
  const sortedData = useMemo(() => {
    if (!data?.items) return [];
    if (!localSortField) return data.items;

    return [...data.items].sort((a, b) => {
      const aVal = a[localSortField];
      const bVal = b[localSortField];

      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;

      let comparison = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return localSortOrder === 'descend' ? -comparison : comparison;
    });
  }, [data?.items, localSortField, localSortOrder]);

  // Update global filters for provider detail navigation
  const updateGlobalFilters = (reportType, newFilters) => {
    dispatch({
      type: SET_REPORT_FILTERS,
      payload: {
        reportType,
        filters: newFilters
      }
    });
  };

  // Table columns with responsive configuration
  const columns = [
    {
      key: 'RecordDate',
      title: 'Fecha',
      dataIndex: 'RecordDate',
      sorter: true,
      width: 120,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      render: (value) => (
        <Text style={{ fontWeight: 500, color: '#374151' }}>{formatDate(value)}</Text>
      )
    },
    ...(filters.groupByNit ? [{
      title: 'NIT',
      dataIndex: 'Nit',
      key: 'Nit',
      width: 120,
      responsive: ['sm', 'md', 'lg', 'xl'],
      render: (value) => (
        <Tag color="blue" style={{ fontSize: isXs ? 11 : 12, fontWeight: 500 }}>
          {value || '-'}
        </Tag>
      )
    }, {
      title: 'Proveedor',
      dataIndex: 'ProjectName',
      key: 'ProjectName',
      width: 180,
      responsive: ['sm', 'md', 'lg', 'xl'],
      render: (value) => (
        <Text style={{ fontSize: isXs ? 11 : 12, fontWeight: 500 }}>
          {value || '-'}
        </Text>
      )
    }] : []),
    {
      title: 'Negro',
      dataIndex: 'NegroCount',
      key: 'NegroCount',
      width: 110,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      render: (value, row) => (
        <Tooltip title={`SKUs con inventario < 10%`}>
          <Space size={4}>
            <Badge
              count={value}
              style={{
                backgroundColor: '#1f2937',
                fontSize: isXs ? 11 : 12,
                fontWeight: 600,
                padding: '0 6px',
                height: '20px',
                lineHeight: '20px'
              }}
            />
            <Text type="secondary" style={{ fontSize: isXs ? 11 : 12 }}>
              {formatPercent(row.NegroPercent)}
            </Text>
          </Space>
        </Tooltip>
      )
    },
    {
      title: 'Rojo',
      dataIndex: 'RojoCount',
      key: 'RojoCount',
      width: 110,
      responsive: ['sm', 'md', 'lg', 'xl'],
      render: (value, row) => (
        <Tooltip title={`SKUs con inventario 10% - 30%`}>
          <Space size={4}>
            <Badge
              count={value}
              style={{
                backgroundColor: '#dc2626',
                fontSize: isXs ? 11 : 12,
                fontWeight: 600,
                padding: '0 6px',
                height: '20px',
                lineHeight: '20px'
              }}
            />
            <Text type="secondary" style={{ fontSize: isXs ? 11 : 12 }}>
              {formatPercent(row.RojoPercent)}
            </Text>
          </Space>
        </Tooltip>
      )
    },
    {
      title: 'Amarillo',
      dataIndex: 'AmarilloCount',
      key: 'AmarilloCount',
      width: 110,
      responsive: ['md', 'lg', 'xl'],
      render: (value, row) => (
        <Tooltip title={`SKUs con inventario 30% - 70%`}>
          <Space size={4}>
            <Badge
              count={value}
              style={{
                backgroundColor: '#ca8a04',
                fontSize: isXs ? 11 : 12,
                fontWeight: 600,
                padding: '0 6px',
                height: '20px',
                lineHeight: '20px'
              }}
            />
            <Text type="secondary" style={{ fontSize: isXs ? 11 : 12 }}>
              {formatPercent(row.AmarilloPercent)}
            </Text>
          </Space>
        </Tooltip>
      )
    },
    {
      title: 'Verde',
      dataIndex: 'VerdeCount',
      key: 'VerdeCount',
      width: 110,
      responsive: ['lg', 'xl'],
      render: (value, row) => (
        <Tooltip title={`SKUs con inventario 70% - 100%`}>
          <Space size={4}>
            <Badge
              count={value}
              style={{
                backgroundColor: '#16a34a',
                fontSize: isXs ? 11 : 12,
                fontWeight: 600,
                padding: '0 6px',
                height: '20px',
                lineHeight: '20px'
              }}
            />
            <Text type="secondary" style={{ fontSize: isXs ? 11 : 12 }}>
              {formatPercent(row.VerdePercent)}
            </Text>
          </Space>
        </Tooltip>
      )
    },
    {
      title: 'Azul',
      dataIndex: 'AzulCount',
      key: 'AzulCount',
      width: 110,
      responsive: ['lg', 'xl'],
      render: (value, row) => (
        <Tooltip title={`SKUs con inventario > 100%`}>
          <Space size={4}>
            <Badge
              count={value}
              style={{
                backgroundColor: '#2563eb',
                fontSize: isXs ? 11 : 12,
                fontWeight: 600,
                padding: '0 6px',
                height: '20px',
                lineHeight: '20px'
              }}
            />
            <Text type="secondary" style={{ fontSize: isXs ? 11 : 12 }}>
              {formatPercent(row.AzulPercent)}
            </Text>
          </Space>
        </Tooltip>
      )
    },
    {
      title: 'Total',
      dataIndex: 'TotalReferences',
      key: 'TotalReferences',
      width: 90,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      render: (value) => (
        <div style={{
          backgroundColor: '#f3f4f6',
          borderRadius: '8px',
          padding: '4px 10px',
          textAlign: 'center',
          minWidth: '50px'
        }}>
          <Text strong style={{ fontSize: isXs ? 12 : 13, color: '#1f2937' }}>
            {value}
          </Text>
        </div>
      )
    },
    {
      title: '% Prom. Inv.',
      dataIndex: 'AvgPercentInventory',
      key: 'AvgPercentInventory',
      width: 110,
      responsive: ['sm', 'md', 'lg', 'xl'],
      render: (value) => {
        const numVal = parseFloat(value);
        let color = '#dc2626';
        if (numVal > 80) color = '#2563eb';
        else if (numVal > 60) color = '#16a34a';
        else if (numVal > 30) color = '#ca8a04';
        else if (numVal > 10) color = '#dc2626';
        return (
          <Tooltip title={`Promedio de % inventario de todos los SKUs`}>
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '4px 10px',
              textAlign: 'center',
              minWidth: '60px'
            }}>
              <Text strong style={{ fontSize: isXs ? 12 : 13, color }}>
                {formatPercent(value)}
              </Text>
            </div>
          </Tooltip>
        );
      }
    }
  ];

  // Professional Loading Skeleton
  const LoadingSkeleton = () => (
    <div style={{
      padding: isXs ? 16 : 24,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '24px',
        padding: isXs ? '32px 24px' : '48px 40px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        maxWidth: '400px',
        width: '90%',
        textAlign: 'center'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 24px',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: '4px solid #e5e7eb',
            borderRadius: '50%',
            borderTopColor: '#667eea',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '32px'
          }}>
            <LoadingOutlined style={{ color: '#667eea' }} />
          </div>
        </div>
        <Title level={isXs ? 4 : 3} style={{
          marginBottom: 12,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 700
        }}>
          Cargando Reporte
        </Title>
        <Paragraph style={{
          fontSize: isXs ? 14 : 16,
          color: '#6b7280',
          marginBottom: 24
        }}>
          Analizando distribución de inventario...
        </Paragraph>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#667eea',
                animation: `pulse 1.5s ease-in-out ${i * 0.2}s infinite`
              }}
            />
          ))}
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 0.4; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.2); }
          }
        `}</style>
      </div>
    </div>
  );

  // Loading state
  if (isLoading && !data) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (isError) {
    return (
      <div style={{
        padding: isXs ? 16 : 24,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
      }}>
        <Card
          style={{
            maxWidth: '500px',
            width: '90%',
            borderRadius: '16px',
                width: '100%',
            boxShadow: '0 20px 60px rgba(220, 38, 38, 0.2)',
            border: '2px solid #dc2626'
          }}
          bodyStyle={{ padding: isXs ? 24 : 32 }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '64px',
              marginBottom: 16
            }}>
              {'⚠️'}
            </div>
            <Title level={isXs ? 4 : 3} style={{ color: '#dc2626', marginBottom: 12 }}>
              Error al Cargar Datos
            </Title>
            <Paragraph style={{ fontSize: isXs ? 14 : 16, color: '#6b7280', marginBottom: 24 }}>
              {error?.message || 'Ocurrió un error al cargar los datos del reporte. Por favor intenta nuevamente.'}
            </Paragraph>
            <Button
              type="primary"
              danger
              size="large"
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              style={{ borderRadius: '8px', height: '44px' }}
            >
              Reintentar
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // No date range selected
  if (!filters.dateRange?.initialDate || !filters.dateRange?.finalDate) {
    return (
      <div style={{
        padding: isXs ? 16 : 24,
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Hero Header */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            padding: isXs ? '32px 24px' : '48px 40px',
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-50%',
              right: '-10%',
              width: '300px',
              height: '300px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              filter: 'blur(40px)'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30%',
              left: '-5%',
              width: '200px',
              height: '200px',
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '50%',
              filter: 'blur(30px)'
            }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                width: '100%',
                background: 'rgba(255, 255, 255, 0.2)',
                marginBottom: '20px',
                fontSize: '32px'
              }}>
                <DashboardOutlined />
              </div>
              <Title level={isXs ? 3 : 2} style={{
                color: 'white',
                marginBottom: 12,
                fontWeight: 700
              }}>
                Distribución Diaria de Inventario
              </Title>
              <Paragraph style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: isXs ? 14 : 16,
                marginBottom: 0,
                maxWidth: '600px'
              }}>
                Análisis detallado de SKUs por rangos de inventario con visualizaciones interactivas y métricas ejecutivas
              </Paragraph>
            </div>
          </div>

          <FiltersPanel
            reportType="dailyInventoryDistribution"
            filters={filters}
            onFiltersChange={updateFilter}
            onApply={handleApplyFilters}
            onReset={handleReset}
            project={user?.attributes?.['custom:project'] || '1'}
          />

          <Alert
            message={
              <span style={{ fontSize: isXs ? 14 : 15, fontWeight: 500 }}>
                Selecciona un rango de fechas
              </span>
            }
            description="Este reporte requiere un rango de fechas para mostrar los resultados."
            type="info"
            showIcon
            style={{
              borderRadius: '12px',
              border: '1px solid #bfdbfe',
              backgroundColor: '#eff6ff'
            }}
          />
        </Space>
      </div>
    );
  }

  return (
    <div style={{
      padding: isXs ? 16 : 24,
      maxWidth: '100%',
      overflow: 'hidden',
      background: '#f9fafb',
      minHeight: '100vh'
    }}>
      <Space direction="vertical" size={isXs ? 'middle' : 'large'} style={{ width: '100%' }}>
        {/* Hero Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '20px',
          padding: isXs ? '24px 20px' : '32px 28px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(102, 126, 234, 0.2)'
        }}>
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '300px',
            height: '300px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            filter: 'blur(40px)'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-30%',
            left: '-5%',
            width: '200px',
            height: '200px',
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '50%',
            filter: 'blur(30px)'
          }} />
          <Row gutter={[16, 16]} align="middle" style={{ position: 'relative', zIndex: 1 }}>
            <Col xs={24} md={16}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isXs ? '48px' : '56px',
                  height: isXs ? '48px' : '56px',
                  borderRadius: '14px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  fontSize: isXs ? '24px' : '28px'
                }}>
                  <DashboardOutlined />
                </div>
                <div>
                  <Title level={isXs ? 4 : 2} style={{
                    color: 'white',
                    marginBottom: 4,
                    fontWeight: 700,
                    fontSize: isXs ? 18 : 24
                  }}>
                    Distribución Diaria de Inventario
                  </Title>
                </div>
              </div>
              <Paragraph style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: isXs ? 13 : 14,
                marginBottom: 0,
                display: 'block'
              }}>
                {filters.onlyActive
                  ? 'Agrupación de SKUs activos por rangos de inventario'
                  : 'Agrupación de todos los SKUs por rangos de inventario'}
                {filters.groupByNit ? ' (separado por proveedor)' : filters.onlyActive ? ' (consolidado)' : ''}
              </Paragraph>
              <Space wrap style={{ marginTop: 12 }}>
                {filters.onlyActive && (
                  <Tag color="success" style={{ fontSize: isXs ? 11 : 12, padding: '4px 10px', borderRadius: '6px' }}>
                    Solo Activos
                  </Tag>
                )}
                {filters.groupByNit && (
                  <Tag color="blue" style={{ fontSize: isXs ? 11 : 12, padding: '4px 10px', borderRadius: '6px' }}>
                    Por Proveedor
                  </Tag>
                )}
                {filters.articleType && filters.articleType.length > 0 && (
                  <Tag color="purple" style={{ fontSize: isXs ? 11 : 12, padding: '4px 10px', borderRadius: '6px' }}>
                    Tipos: {filters.articleType.join(', ')}
                  </Tag>
                )}
                {filters.category && filters.category.length > 0 && (
                  <Tag color="orange" style={{ fontSize: isXs ? 11 : 12, padding: '4px 10px', borderRadius: '6px' }}>
                    Categorias: {filters.category.join(', ')}
                  </Tag>
                )}
              </Space>
            </Col>
            <Col xs={24} md={8}>
              <Space
                wrap
                style={{ width: '100%', justifyContent: isXs ? 'flex-start' : 'flex-end' }}
              >
                {filters.groupByNit && filters.nitList && filters.nitList.length > 0 && (
                  <Button
                    size={isXs ? 'small' : 'middle'}
                    onClick={() => {
                      // Envía todos los filtros actuales aplicados al reporte de detalle
                      updateGlobalFilters('providerInventoryDetail', {
                        // Filtros de fecha (copiar el objeto completo)
                        dateRange: filters.dateRange ? { ...filters.dateRange } : null,
                        // Lista de proveedores seleccionados
                        nitList: [...filters.nitList],
                        // Configuración de agrupación (en detalle se ve por SKU individual)
                        groupByNit: false,
                        // Filtro de SKUs activos
                        onlyActive: filters.onlyActive ?? true,
                        // Indicador de que viene desde el reporte agrupado
                        fromGroupedView: true,
                        // NIT principal (primer proveedor seleccionado por defecto)
                        nit: filters.nitList[0] || '1'
                      });
                      navigate('/provider-inventory-detail');
                    }}
                    style={{
                      display: 'inline-flex',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      borderRadius: '8px'
                    }}
                  >
                    Ver Detalle <RightOutlined />
                  </Button>
                )}
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  size={isXs ? 'small' : 'middle'}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    borderRadius: '8px'
                  }}
                >
                  {isSm ? 'Actualizar' : ''}
                </Button>
                <ExportButton
                  reportType="dailyInventoryDistribution"
                  filters={filters}
                  disabled={!data?.items || data.items.length === 0}
                />
              </Space>
            </Col>
          </Row>
        </div>

        {/* Filters */}
        <Card
          style={{
            width: '100%',
            borderRadius: '16px',
                width: '100%',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb'
          }}
          bodyStyle={{ padding: isXs ? 16 : 20 }}
        >
          <FiltersPanel
            reportType="dailyInventoryDistribution"
            filters={filters}
            onFiltersChange={updateFilter}
            onApply={handleApplyFilters}
            onReset={handleReset}
            loading={isLoading}
            project={user?.attributes?.['custom:project'] || '1'}
          />
        </Card>

        {/* Charts */}
        {data?.items && data.items.length > 0 && (
          <>
            {/* Floating Color Glossary */}
            <Card
              size="small"
              style={{
                width: '100%',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
                backgroundColor: '#ffffff',
                position: 'sticky',
                top: 0,
                zIndex: 10
              }}
              bodyStyle={{ padding: isXs ? '10px 14px' : '12px 20px' }}
            >
              <Text style={{ fontSize: isXs ? 11 : 12, fontWeight: 600, color: '#374151', marginRight: 12 }}>
                Guia de colores:
              </Text>
              <Space wrap size={isXs ? 6 : 10}>
                <Tooltip title="Sin stock practicamente - Pedido urgente">
                  <Tag color="#1f2937" style={{ fontSize: isXs ? 10 : 11, padding: '2px 8px', borderRadius: '4px', color: '#fff' }}>
                    Negro (&lt;10%)
                  </Tag>
                </Tooltip>
                <Tooltip title="Stock muy bajo - Pedir pronto">
                  <Tag color="#dc2626" style={{ fontSize: isXs ? 10 : 11, padding: '2px 8px', borderRadius: '4px', color: '#fff' }}>
                    Rojo (10-30%)
                  </Tag>
                </Tooltip>
                <Tooltip title="Stock moderado - Monitorear">
                  <Tag color="#ca8a04" style={{ fontSize: isXs ? 10 : 11, padding: '2px 8px', borderRadius: '4px', color: '#fff' }}>
                    Amarillo (30-70%)
                  </Tag>
                </Tooltip>
                <Tooltip title="Stock adecuado - Todo bien">
                  <Tag color="#16a34a" style={{ fontSize: isXs ? 10 : 11, padding: '2px 8px', borderRadius: '4px', color: '#fff' }}>
                    Verde (70-100%)
                  </Tag>
                </Tooltip>
                <Tooltip title="Exceso de stock - Posible sobre-inversion">
                  <Tag color="#2563eb" style={{ fontSize: isXs ? 10 : 11, padding: '2px 8px', borderRadius: '4px', color: '#fff' }}>
                    Azul (&gt;100%)
                  </Tag>
                </Tooltip>
              </Space>
            </Card>

            {/* ====== SECCION 1: Graficas Principales ====== */}
            <SectionHeader title="Graficas Principales" sectionKey="main" />
            {!collapsedSections.main && (
              <>
                <Card
                  title={
                    <span style={{ fontSize: isXs ? 16 : 18, fontWeight: 600, color: '#1f2937' }}>
                      Distribucion de Inventario por Rango
                    </span>
                  }
                  extra={<ChartToggleButton chartKey="distribution" />}
                  style={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                    border: '1px solid #e5e7eb'
                  }}
                  bodyStyle={{ padding: isXs ? 16 : 20, display: collapsedCharts.distribution ? 'none' : undefined }}
                >
                  <DistributionChart data={data.items} groupByNit={filters.groupByNit} />
                </Card>

                <Card
                  title={
                    <span style={{ fontSize: isXs ? 16 : 18, fontWeight: 600, color: '#1f2937' }}>
                      Evolucion de Salud del Inventario
                    </span>
                  }
                  extra={<ChartToggleButton chartKey="healthEvolution" />}
                  style={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                    border: '1px solid #e5e7eb'
                  }}
                  bodyStyle={{ padding: isXs ? 16 : 20, display: collapsedCharts.healthEvolution ? 'none' : undefined }}
                >
                  <HealthEvolutionChart
                    data={data.items}
                    selectedNit={filters.nitList?.[0]}
                    groupByNit={filters.groupByNit}
                  />
                </Card>

                <Card
                  title={
                    <span style={{ fontSize: isXs ? 16 : 18, fontWeight: 600, color: '#1f2937' }}>
                      Semaforo de Nivel de Inventario
                    </span>
                  }
                  extra={<ChartToggleButton chartKey="bufferLevel" />}
                  style={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                    border: '1px solid #e5e7eb'
                  }}
                  bodyStyle={{ padding: isXs ? 16 : 20, display: collapsedCharts.bufferLevel ? 'none' : undefined }}
                >
                  <BufferLevelSignalChart
                    data={data.items}
                    selectedNit={filters.nitList?.[0]}
                    groupByNit={filters.groupByNit}
                  />
                </Card>
              </>
            )}

            {/* ====== SECCION 2: Indicadores Clave ====== */}
            <SectionHeader title="Indicadores Clave" sectionKey="kpis" />
            {!collapsedSections.kpis && (
              <Row gutter={[isXs ? 12 : 20]}>
                <Col xs={24} md={12} lg={8}>
                  <Card
                    title={
                      <span style={{ fontSize: isXs ? 14 : 16, fontWeight: 600, color: '#1f2937' }}>
                        Salud del Inventario
                      </span>
                    }
                    extra={<ChartToggleButton chartKey="healthGauge" />}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                      border: '1px solid #e5e7eb'
                    }}
                    bodyStyle={{ padding: isXs ? 16 : 20, display: collapsedCharts.healthGauge ? 'none' : undefined }}
                  >
                    <InventoryHealthGauge
                      data={data.items}
                      selectedNit={filters.nitList?.[0]}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={12} lg={8}>
                  <Card
                    title={
                      <span style={{ fontSize: isXs ? 14 : 16, fontWeight: 600, color: '#1f2937' }}>
                        Tendencia Critica
                      </span>
                    }
                    extra={<ChartToggleButton chartKey="criticalTrend" />}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                      border: '1px solid #e5e7eb'
                    }}
                    bodyStyle={{ padding: isXs ? 16 : 20, display: collapsedCharts.criticalTrend ? 'none' : undefined }}
                  >
                    <CriticalTrendLine
                      data={data.items}
                      selectedNit={filters.nitList?.[0]}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={12} lg={8}>
                  <Card
                    title={
                      <span style={{ fontSize: isXs ? 14 : 16, fontWeight: 600, color: '#1f2937' }}>
                        Ranking de Proveedores
                      </span>
                    }
                    extra={<ChartToggleButton chartKey="providerRanking" />}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                      border: '1px solid #e5e7eb'
                    }}
                    bodyStyle={{ padding: isXs ? 16 : 20, display: collapsedCharts.providerRanking ? 'none' : undefined }}
                  >
                    <ProviderRankingChart
                      data={data.items}
                      groupByNit={filters.groupByNit}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            {/* ====== SECCION 3: Analisis Predictivo ====== */}
            <SectionHeader title="Analisis Predictivo" sectionKey="predictive" />
            {!collapsedSections.predictive && (
              <Row gutter={[isXs ? 12 : 20]}>
                <Col xs={24} lg={12}>
                  <Card
                    title={
                      <span style={{ fontSize: isXs ? 14 : 16, fontWeight: 600, color: '#1f2937' }}>
                        Pronostico de Productos en Riesgo
                      </span>
                    }
                    extra={<ChartToggleButton chartKey="stockout" />}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                      border: '1px solid #e5e7eb'
                    }}
                    bodyStyle={{ padding: isXs ? 16 : 20, display: collapsedCharts.stockout ? 'none' : undefined }}
                  >
                    <StockoutForecastChart
                      data={data.items}
                      selectedNit={filters.nitList?.[0]}
                    />
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card
                    title={
                      <span style={{ fontSize: isXs ? 14 : 16, fontWeight: 600, color: '#1f2937' }}>
                        Ritmo de Mejora del Inventario
                      </span>
                    }
                    extra={<ChartToggleButton chartKey="velocity" />}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                      border: '1px solid #e5e7eb'
                    }}
                    bodyStyle={{ padding: isXs ? 16 : 20, display: collapsedCharts.velocity ? 'none' : undefined }}
                  >
                    <VelocityOfChangeChart
                      data={data.items}
                      selectedNit={filters.nitList?.[0]}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            {/* ====== SECCION 4: Analisis Avanzado ====== */}
            <SectionHeader title="Analisis Avanzado" sectionKey="advanced" />
            {!collapsedSections.advanced && (
              <Row gutter={[isXs ? 12 : 20]}>
                <Col xs={24} lg={12}>
                  <Card
                    title={
                      <span style={{ fontSize: isXs ? 14 : 16, fontWeight: 600, color: '#1f2937' }}>
                        Mapa de Calor por Proveedor
                      </span>
                    }
                    extra={<ChartToggleButton chartKey="heatmap" />}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                      border: '1px solid #e5e7eb'
                    }}
                    bodyStyle={{ padding: isXs ? 16 : 20, display: collapsedCharts.heatmap ? 'none' : undefined }}
                  >
                    <ProviderHeatmap data={data.items} />
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card
                    title={
                      <span style={{ fontSize: isXs ? 14 : 16, fontWeight: 600, color: '#1f2937' }}>
                        Tendencia de Mejora del Inventario
                      </span>
                    }
                    extra={<ChartToggleButton chartKey="momentum" />}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                      border: '1px solid #e5e7eb'
                    }}
                    bodyStyle={{ padding: isXs ? 16 : 20, display: collapsedCharts.momentum ? 'none' : undefined }}
                  >
                    <ImprovementMomentumChart
                      data={data.items}
                      selectedNit={filters.nitList?.[0]}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            {/* ====== SECCION 5: Datos Detallados ====== */}
            <SectionHeader title="Datos Detallados" sectionKey="dataTable" />
            {!collapsedSections.dataTable && (
              <Card
                bodyStyle={{ padding: 0 }}
                style={{
                  width: '100%',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
                }}
              >
              <Table
                columns={columns.map(col => ({
                  ...col,
                  sortOrder: localSortField === col.key || localSortField === col.dataIndex ? localSortOrder : null,
                  title: (
                    <span style={{
                      fontWeight: 600,
                      color: '#374151',
                      fontSize: isXs ? 12 : 13
                    }}>
                      {col.title}
                    </span>
                  )
                }))}
                dataSource={sortedData}
                rowKey={(record, index) => `${record.RecordDate}-${record.Nit || 'all'}-${index}`}
                pagination={{
                  pageSize: isXs ? 10 : 20,
                  showSizeChanger: !isXs,
                  showTotal: (total, range) => isXs
                    ? `${range[0]}-${range[1]} de ${total}`
                    : `Total: ${total} registros`,
                  simple: isXs,
                  style: { padding: '16px 24px' }
                }}
                scroll={{ x: isXs ? 800 : 'max-content' }}
                size={isXs ? 'small' : 'middle'}
                style={{ fontSize: isXs ? 12 : 13 }}
                onChange={(_pagination, _filters, sorter) => {
                  if (sorter.columnKey || sorter.field) {
                    handleSort(sorter.columnKey || sorter.field);
                  }
                }}
                className="detailed-data-table"
                sticky
              />
              <style global>{`
                .detailed-data-table .ant-table-thead > tr > th {
                  background-color: #f9fafb !important;
                  border-bottom: 2px solid #e5e7eb !important;
                  font-weight: 600;
                  color: #374151;
                }
                .detailed-data-table .ant-table-tbody > tr:nth-child(even) {
                  background-color: #f9fafb;
                }
                .detailed-data-table .ant-table-tbody > tr:hover {
                  background-color: #e5e7eb !important;
                }
                .detailed-data-table .ant-table-tbody > tr > td {
                  padding: ${isXs ? '8px 12px' : '12px 16px'};
                }
              `}</style>
            </Card>
            )}

            {/* Legend */}
            <Card
              title={
                <span style={{ fontSize: isXs ? 14 : 16, fontWeight: 600, color: '#374151' }}>
                  Rangos de Inventario - ¿Que significa cada color?
                </span>
              }
              size="small"
              style={{
                borderRadius: '16px',
                width: '100%',
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
              }}
              bodyStyle={{ padding: isXs ? 16 : 20 }}
            >
              <Row gutter={[isXs ? 12 : 24]}>
                <Col xs={12} sm={8} md={4}>
                  <Space size={isXs ? 6 : 10}>
                    <Badge
                      color="#1f2937"
                      style={{
                        width: isXs ? 14 : 18,
                        height: isXs ? 14 : 18,
                        borderRadius: '4px'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: isXs ? 12 : 13, fontWeight: 600, color: '#1f2937' }}>
                        Negro
                      </div>
                      <Text type="secondary" style={{ fontSize: isXs ? 10 : 11 }}>
                        &lt;10% - Sin stock, pedir urgente
                      </Text>
                    </div>
                  </Space>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Space size={isXs ? 6 : 10}>
                    <Badge
                      color="#dc2626"
                      style={{
                        width: isXs ? 14 : 18,
                        height: isXs ? 14 : 18,
                        borderRadius: '4px'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: isXs ? 12 : 13, fontWeight: 600, color: '#dc2626' }}>
                        Rojo
                      </div>
                      <Text type="secondary" style={{ fontSize: isXs ? 10 : 11 }}>
                        10-30% - Muy bajo, pedir pronto
                      </Text>
                    </div>
                  </Space>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Space size={isXs ? 6 : 10}>
                    <Badge
                      color="#ca8a04"
                      style={{
                        width: isXs ? 14 : 18,
                        height: isXs ? 14 : 18,
                        borderRadius: '4px'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: isXs ? 12 : 13, fontWeight: 600, color: '#ca8a04' }}>
                        Amarillo
                      </div>
                      <Text type="secondary" style={{ fontSize: isXs ? 10 : 11 }}>
                        30-70% - Moderado, monitorear
                      </Text>
                    </div>
                  </Space>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Space size={isXs ? 6 : 10}>
                    <Badge
                      color="#16a34a"
                      style={{
                        width: isXs ? 14 : 18,
                        height: isXs ? 14 : 18,
                        borderRadius: '4px'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: isXs ? 12 : 13, fontWeight: 600, color: '#16a34a' }}>
                        Verde
                      </div>
                      <Text type="secondary" style={{ fontSize: isXs ? 10 : 11 }}>
                        70-100% - Stock adecuado
                      </Text>
                    </div>
                  </Space>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Space size={isXs ? 6 : 10}>
                    <Badge
                      color="#2563eb"
                      style={{
                        width: isXs ? 14 : 18,
                        height: isXs ? 14 : 18,
                        borderRadius: '4px'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: isXs ? 12 : 13, fontWeight: 600, color: '#2563eb' }}>
                        Azul
                      </div>
                      <Text type="secondary" style={{ fontSize: isXs ? 10 : 11 }}>
                        &gt;100% - Exceso de stock
                      </Text>
                    </div>
                  </Space>
                </Col>
              </Row>
            </Card>
          </>
        )}

        {/* No data message */}
        {(!data?.items || data.items.length === 0) && !isLoading && (
          <Card
            style={{
              borderRadius: '16px',
                width: '100%',
              border: '2px dashed #d1d5db',
              backgroundColor: '#f9fafb',
              textAlign: 'center',
              padding: isXs ? 32 : 48
            }}
          >
            <div style={{ fontSize: 64, marginBottom: 16 }}>{'📊'}</div>
            <Title level={isXs ? 4 : 3} style={{ color: '#6b7280', marginBottom: 12 }}>
              No se encontraron datos
            </Title>
            <Paragraph style={{ fontSize: isXs ? 14 : 16, color: '#9ca3af' }}>
              Verifica que existan registros en el rango de fechas seleccionado.
            </Paragraph>
          </Card>
        )}
      </Space>
    </div>
  );
}

export default DailyInventoryDistributionReport;
